import { Stack } from "@mui/material";
import { Alert, ErrorBoundary } from "@platform/design-system";
import {
  RumiLaunchpad,
  RumiReady,
  RumiRunFailed,
  RumiRunning,
  toLaunchpadRun,
} from "@platform/rumi-ui";
import { useParams } from "react-router";

import { Loader } from "../../../../components";
import { ROUTE_NAMES } from "../../../../constants";

import { useRfpFacts } from "./useRfpFacts";
import { useRumiRun } from "./useRumiRun";

import type { RumiNullableRumiRun } from "@platform/api";
import type {
  CoverageSection,
  GeneralSection,
  RumiRun,
  SectionLink,
} from "@platform/rumi-ui";

const AQUA_GRID_LABEL = "View in AQUA grid";

const rfpPath = (rfpId: string): string => `/${ROUTE_NAMES.RFPS}/${rfpId}`;

const rfpDetailsLink = (rfpId: string): SectionLink => ({
  label: "View RFP details",
  href: `${rfpPath(rfpId)}/${ROUTE_NAMES.RFP_WORKBENCH_DETAILS}`,
});

// Each General RFP Data section opens the screen that holds its data: the
// client and RFP fields live on Details, the documents in Build's library.
const GENERAL_SECTION_LINKS: ReadonlyMap<
  string,
  (rfpId: string) => SectionLink
> = new Map([
  ["client-details", rfpDetailsLink],
  ["rfp-and-broker-details", rfpDetailsLink],
  [
    "documents",
    (rfpId: string) => ({
      label: "View documents",
      href: `${rfpPath(rfpId)}/${ROUTE_NAMES.RFP_WORKBENCH_BUILD_2}`,
    }),
  ],
]);

const withGeneralLink =
  (rfpId: string) =>
  (section: GeneralSection): GeneralSection => {
    const toLink = GENERAL_SECTION_LINKS.get(section.id);
    return toLink ? { ...section, link: toLink(rfpId) } : section;
  };

// A coverage with no plans has no grid to land on.
const withGridLink =
  (rfpId: string) =>
  (section: CoverageSection): CoverageSection =>
    section.planCount > 0
      ? {
          ...section,
          link: {
            label: AQUA_GRID_LABEL,
            href: `${rfpPath(rfpId)}/${ROUTE_NAMES.RFP_WORKBENCH_BUILD_2}/${ROUTE_NAMES.RFP_WORKBENCH_BUILD_2_FORMS}/${section.coverageType}`,
          },
        }
      : section;

/** Points each section at the screen where the analyst can change its data. */
const withSectionLinks = (run: RumiRun): RumiRun => ({
  ...run,
  generalSections: run.generalSections.map(withGeneralLink(run.rfpId)),
  coverageSections: run.coverageSections.map(withGridLink(run.rfpId)),
});

/** A Run that exists; the read answers null for an RFP that has never had one. */
type LatestRun = NonNullable<RumiNullableRumiRun>;

/** What the ended Run found, beside the RFP's own details. */
const CompletedRun = ({
  rfpId,
  run,
  onRerun,
}: {
  rfpId: string;
  run: LatestRun;
  onRerun: () => void;
}): React.JSX.Element => {
  const rfp = useRfpFacts(rfpId);

  switch (rfp.kind) {
    case "loading":
      return <Loader />;
    case "loadFailed":
      return (
        <Alert severity="error" title="RFP Details Unavailable">
          The Launchpad couldn&apos;t load this RFP&apos;s details. Refresh to
          try again.
        </Alert>
      );
    case "ready":
      return (
        <RumiLaunchpad
          run={withSectionLinks(toLaunchpadRun(run, rfp.facts))}
          onRerun={onRerun}
        />
      );
  }
};

const RunView = ({
  rfpId,
  run,
  onRun,
}: {
  rfpId: string;
  run: RumiNullableRumiRun;
  onRun: () => void;
}): React.JSX.Element => {
  if (run === null) {
    return <RumiReady onRun={onRun} />;
  }
  if (run.open) {
    return <RumiRunning startedAt={new Date(run.startedAt)} />;
  }
  if (run.outcome === "completed") {
    return <CompletedRun rfpId={rfpId} run={run} onRerun={onRun} />;
  }
  return <RumiRunFailed onRun={onRun} reason={run.errorSummary ?? undefined} />;
};

const RfpLaunchpad = ({ rfpId }: { rfpId: string }): React.JSX.Element => {
  const { state, start, startFailed } = useRumiRun(rfpId);

  switch (state.kind) {
    case "loading":
      return <Loader />;
    case "notAvailable":
      return (
        <Alert severity="info" title="Rumi Not Available">
          Rumi isn&apos;t available for your role.
        </Alert>
      );
    case "loadFailed":
      return (
        <Alert severity="error" title="Rumi Unavailable">
          The Launchpad couldn&apos;t load Rumi&apos;s latest run. Refresh to
          try again.
        </Alert>
      );
    case "ready":
      return (
        <Stack spacing={3}>
          {startFailed && (
            <Alert severity="error" title="Rumi Didn't Start">
              Rumi couldn&apos;t start a run. Try again.
            </Alert>
          )}
          <RunView rfpId={rfpId} run={state.run} onRun={start} />
        </Stack>
      );
  }
};

/**
 * The Rumi Launchpad tab.
 *
 * Run Rumi starts a real Run through the BFF, and the tab shows the RFP's
 * latest Run: ready, running, failed with rumi-api's reason, or finished with
 * what Rumi found beside the RFP's details. The tab is gated on the
 * `rumi-launchpad` flag, off in production.
 */
const RumiLaunchpadRoute = (): React.JSX.Element => {
  const { id: rfpId } = useParams<{ id: string }>();

  // Keyed so moving between RFPs reads each one's own run state afresh.
  return (
    <ErrorBoundary>
      <RfpLaunchpad key={rfpId} rfpId={rfpId!} />
    </ErrorBoundary>
  );
};

export { RumiLaunchpadRoute };
